import { z } from "zod";

/**
 * Schema pieces more than one context needs, so that one question has one answer.
 *
 * Created by feature 008, when Properties became the second context to carry `date` columns and the
 * choice was between importing savings' definition — which would make one pillar's wire contract
 * depend on another's — and writing a second regex that could drift from the first. Neither is what
 * `Valuation`'s own comment argues for: the shared kernel is where the single implementation lives.
 *
 * Keep this small. A thing belongs here once a SECOND context needs it, not in anticipation of one.
 */

/**
 * `YYYY-MM-DD` — a calendar date, with no time and no timezone.
 *
 * NOT `z.iso.datetime()`, and the difference is not stylistic. A `date` column read as a `Date` sits
 * at UTC midnight, and asking that `Date` for its month with a local-time accessor returns the
 * PREVIOUS month for any process running behind UTC. Savings met this first with `as_of_date`, where
 * it silently moved a balance a month earlier on the chart; Properties has three such columns —
 * `purchase_date`, `valuation_date` and `start_date` — and there a one-day shift changes the elapsed
 * month count, and therefore what the household is told they still owe.
 *
 * Held as a string it compares and buckets exactly, because ISO dates sort lexicographically.
 */
export const CalendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD.");

/**
 * Two decimal places at most — decided on the DECIMAL STRING, never by multiplying by 100.
 *
 * `value * 100 === Math.round(value * 100)` is the obvious test and it is wrong: it rejects 36,702
 * of the 400,001 legitimate two-decimal amounts between -2000.00 and 2000.00, among them €0.07,
 * €0.29, €4.35 and €1,234,567.89. Those values are not representable in binary64, so multiplying
 * lands a hair off the integer and the comparison fails. It looks correct in a spot check because
 * whether it fails depends on the value, and 12.50 works.
 *
 * `String(value)` gives the shortest round-tripping representation, so a number parsed from a JSON
 * literal with two decimals stringifies back to two decimals. Exponential form is refused outright:
 * `1e-7` has no `.` and would otherwise slip through as "no decimals at all".
 */
export function hasAtMostTwoDecimals(value: number): boolean {
  const text = String(value);
  if (text.includes("e") || text.includes("E")) return false;
  const point = text.indexOf(".");
  return point === -1 || text.length - point - 1 <= 2;
}

/**
 * The magnitude a `numeric(15,2)` column holds.
 *
 * Every monetary column in this product is `numeric(15,2)` —
 * `docs/project-overview/data-model.md` §Monetary Precision fixes it for balances, expenses, stock
 * prices, valuations, mortgage principals and derived interest alike — so there is one limit rather
 * than one per context.
 */
export const MONEY_LIMIT = 9_999_999_999_999.99;
